import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import PinModal from '../components/PinModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, UserCheck, UserX, KeyRound, Shield, AlertTriangle, Copy, Check, Edit, Phone, Stethoscope, Activity, ClipboardList, IdCard, GraduationCap, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 6.5: Added cashier and inventory_manager roles
const ROLE_ICONS = {
  doctor: <Stethoscope className="w-4 h-4" />,
  nurse: <Activity className="w-4 h-4" />,
  reception: <ClipboardList className="w-4 h-4" />,
  cashier: <DollarSign className="w-4 h-4" />,
  inventory_manager: <Package className="w-4 h-4" />
};

const ROLE_COLORS = {
  doctor: 'bg-blue-100 text-blue-800 border-blue-200',
  nurse: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  reception: 'bg-violet-100 text-violet-800 border-violet-200',
  cashier: 'bg-amber-100 text-amber-800 border-amber-200',
  inventory_manager: 'bg-slate-100 text-slate-800 border-slate-200'
};

const ROLE_LABELS = {
  doctor: 'Doctor',
  nurse: 'Nurse',
  reception: 'Reception',
  cashier: 'Cashier',
  inventory_manager: 'Inventory Mgr'
};

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPinSetupModal, setShowPinSetupModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  const [newStaff, setNewStaff] = useState({
    full_name: '',
    email: '',
    telephone: '',
    role: 'reception'
  });
  
  const [editStaff, setEditStaff] = useState({
    full_name: '',
    telephone: '',
    professional_qualification: '',
    government_id_number: ''
  });
  
  const [newPin, setNewPin] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [staffRes, pinStatusRes] = await Promise.all([
        axios.get(`${API}/admin/staff`),
        axios.get(`${API}/admin/pin-status`)
      ]);
      setStaff(staffRes.data);
      setPinSet(pinStatusRes.data.pin_set);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/staff`, newStaff);
      setStaff([response.data.user, ...staff]);
      setTempPassword(response.data.temporary_password);
      setShowAddModal(false);
      setShowPasswordModal(true);
      setNewStaff({ full_name: '', email: '', telephone: '', role: 'reception' });
      toast.success('Staff member created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStaff = (member) => {
    setSelectedStaff(member);
    setEditStaff({
      full_name: member.full_name || '',
      telephone: member.telephone || '',
      professional_qualification: member.professional_qualification || '',
      government_id_number: member.government_id_number || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        full_name: editStaff.full_name,
        telephone: editStaff.telephone
      };
      
      // Only include doctor-specific fields for doctors
      if (selectedStaff.role === 'doctor') {
        updateData.professional_qualification = editStaff.professional_qualification;
        updateData.government_id_number = editStaff.government_id_number;
      }
      
      const response = await axios.put(`${API}/admin/staff/${selectedStaff.id}/profile`, updateData);
      setStaff(staff.map(s => s.id === selectedStaff.id ? response.data : s));
      setShowEditModal(false);
      setSelectedStaff(null);
      toast.success('Staff profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    setPinError('');
    
    if (!newPin || newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      setPinError('PIN must be 4-6 digits');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/pin`, { pin: newPin });
      setPinSet(true);
      setShowPinSetupModal(false);
      setNewPin('');
      toast.success('Admin PIN set successfully');
    } catch (error) {
      setPinError(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (staffId, staffName, currentStatus) => {
    setPendingAction({
      type: 'status',
      staffId,
      staffName,
      newStatus: !currentStatus
    });
    setShowPinModal(true);
  };

  const handleResetPassword = (staffId, staffName) => {
    setPendingAction({
      type: 'reset_password',
      staffId,
      staffName
    });
    setShowPinModal(true);
  };

  const executeAction = async (pin) => {
    if (!pendingAction) return;
    
    setSaving(true);
    try {
      if (pendingAction.type === 'status') {
        await axios.put(`${API}/admin/staff/${pendingAction.staffId}/status`, {
          is_active: pendingAction.newStatus
        });
        setStaff(staff.map(s => 
          s.id === pendingAction.staffId 
            ? { ...s, is_active: pendingAction.newStatus }
            : s
        ));
        toast.success(`Staff ${pendingAction.newStatus ? 'activated' : 'deactivated'}`);
      } else if (pendingAction.type === 'reset_password') {
        const response = await axios.post(`${API}/admin/staff/${pendingAction.staffId}/reset-password`, { pin });
        setTempPassword(response.data.temporary_password);
        setShowPasswordModal(true);
        toast.success('Password reset successfully');
      }
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="staff-management-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Staff Management</h1>
            <p className="text-slate-600 mt-1">Manage clinic staff accounts</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowPinSetupModal(true)}
              className="flex items-center gap-2 border-2"
              data-testid="set-pin-btn"
            >
              <Shield className="w-4 h-4" />
              {pinSet ? 'Change PIN' : 'Set PIN'}
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-staff-btn"
            >
              <Plus className="w-4 h-4" />
              Add Staff
            </Button>
          </div>
        </div>

        {/* PIN Warning */}
        {!pinSet && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Admin PIN Required</p>
              <p className="text-sm text-amber-700">Please set an Admin PIN to enable password resets and other critical actions.</p>
            </div>
          </div>
        )}

        {/* Staff List */}
        <div className="card" data-testid="staff-list">
          <div className="p-4 border-b-2 border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Staff Members ({staff.length})
            </h2>
          </div>
          
          {staff.length > 0 ? (
            <div className="divide-y-2 divide-slate-100">
              {staff.map((member) => (
                <div
                  key={member.id}
                  className="p-4 flex items-center justify-between hover:bg-slate-50"
                  data-testid={`staff-row-${member.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      member.is_active 
                        ? ROLE_COLORS[member.role]?.split(' ')[0] || 'bg-slate-100'
                        : 'bg-slate-100 text-slate-400'
                    } ${member.is_active ? ROLE_COLORS[member.role]?.split(' ')[1] || 'text-slate-700' : ''}`}>
                      {member.full_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{member.full_name}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <span>{member.email}</span>
                        {member.telephone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.telephone}
                          </span>
                        )}
                      </div>
                      {member.role === 'doctor' && (member.professional_qualification || member.government_id_number) && (
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          {member.professional_qualification && (
                            <span className="flex items-center gap-1">
                              <GraduationCap className="w-3 h-3" />
                              {member.professional_qualification}
                            </span>
                          )}
                          {member.government_id_number && (
                            <span className="flex items-center gap-1">
                              <IdCard className="w-3 h-3" />
                              {member.government_id_number}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1.5 ${
                      ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-800 border-slate-200'
                    }`}>
                      {ROLE_ICONS[member.role]}
                      {ROLE_LABELS[member.role] || member.role}
                    </span>
                    
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      member.is_active
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditStaff(member)}
                        className="border-2"
                        data-testid={`edit-staff-${member.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(member.id, member.full_name, member.is_active)}
                        className="border-2"
                        data-testid={`toggle-status-${member.id}`}
                      >
                        {member.is_active ? (
                          <><UserX className="w-4 h-4 mr-1" /> Deactivate</>
                        ) : (
                          <><UserCheck className="w-4 h-4 mr-1" /> Activate</>
                        )}
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPassword(member.id, member.full_name)}
                        disabled={!pinSet}
                        className="border-2"
                        data-testid={`reset-password-${member.id}`}
                      >
                        <KeyRound className="w-4 h-4 mr-1" />
                        Reset Password
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No staff members yet</p>
              <p className="text-sm text-slate-500">Add your first staff member to get started</p>
            </div>
          )}
        </div>

        {/* Add Staff Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900">Add New Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddStaff} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={newStaff.full_name}
                  onChange={(e) => setNewStaff({ ...newStaff, full_name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  data-testid="staff-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                  data-testid="staff-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telephone">Telephone</Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={newStaff.telephone}
                  onChange={(e) => setNewStaff({ ...newStaff, telephone: e.target.value })}
                  placeholder="Enter telephone number"
                  data-testid="staff-telephone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newStaff.role}
                  onValueChange={(value) => setNewStaff({ ...newStaff, role: value })}
                >
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="doctor">
                      <span className="flex items-center gap-2">
                        <Stethoscope className="w-4 h-4" /> Doctor
                      </span>
                    </SelectItem>
                    <SelectItem value="nurse">
                      <span className="flex items-center gap-2">
                        <Activity className="w-4 h-4" /> Nurse
                      </span>
                    </SelectItem>
                    <SelectItem value="reception">
                      <span className="flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" /> Reception
                      </span>
                    </SelectItem>
                    <SelectItem value="cashier">
                      <span className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Cashier
                      </span>
                    </SelectItem>
                    <SelectItem value="inventory_manager">
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4" /> Inventory Manager
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                  data-testid="create-staff-btn"
                >
                  {saving ? 'Creating...' : 'Create Staff'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Staff Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                Edit Staff Profile
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveEdit} className="space-y-4 pt-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 border">
                {ROLE_ICONS[selectedStaff?.role]}
                <span className="font-medium">{ROLE_LABELS[selectedStaff?.role]}</span>
                <span className="text-slate-400">•</span>
                <span>{selectedStaff?.email}</span>
              </div>
              
              <div>
                <Label htmlFor="edit_full_name">Full Name</Label>
                <Input
                  id="edit_full_name"
                  value={editStaff.full_name}
                  onChange={(e) => setEditStaff({ ...editStaff, full_name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  data-testid="edit-staff-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit_telephone">Telephone</Label>
                <Input
                  id="edit_telephone"
                  type="tel"
                  value={editStaff.telephone}
                  onChange={(e) => setEditStaff({ ...editStaff, telephone: e.target.value })}
                  placeholder="Enter telephone number"
                  data-testid="edit-staff-telephone-input"
                />
              </div>
              
              {/* Doctor-specific compliance fields */}
              {selectedStaff?.role === 'doctor' && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-blue-600" />
                      Doctor Compliance Fields
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="professional_qualification" className="flex items-center gap-1.5">
                          <GraduationCap className="w-4 h-4" />
                          Professional Qualification
                        </Label>
                        <Input
                          id="professional_qualification"
                          value={editStaff.professional_qualification}
                          onChange={(e) => setEditStaff({ ...editStaff, professional_qualification: e.target.value })}
                          placeholder="e.g., MBBS, MD, DO"
                          data-testid="edit-staff-qualification-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="government_id_number" className="flex items-center gap-1.5">
                          <IdCard className="w-4 h-4" />
                          Government ID / License Number
                        </Label>
                        <Input
                          id="government_id_number"
                          value={editStaff.government_id_number}
                          onChange={(e) => setEditStaff({ ...editStaff, government_id_number: e.target.value })}
                          placeholder="Enter license or ID number"
                          data-testid="edit-staff-govid-input"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedStaff(null);
                  }}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                  data-testid="save-edit-staff-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Set PIN Modal */}
        <Dialog open={showPinSetupModal} onOpenChange={setShowPinSetupModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-900">
                <Shield className="w-5 h-5 text-blue-600" />
                {pinSet ? 'Change Admin PIN' : 'Set Admin PIN'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSetPin} className="space-y-4 pt-4">
              <p className="text-sm text-slate-600">
                This PIN will be required for critical actions like password resets and deleting records.
              </p>
              
              <div>
                <Label htmlFor="pin">PIN (4-6 digits)</Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setNewPin(val);
                    setPinError('');
                  }}
                  placeholder="Enter 4-6 digit PIN"
                  className="text-center text-lg tracking-widest"
                  data-testid="pin-setup-input"
                />
              </div>
              
              {pinError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
                  <AlertTriangle className="w-4 h-4" />
                  {pinError}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPinSetupModal(false);
                    setNewPin('');
                    setPinError('');
                  }}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving || newPin.length < 4}
                  data-testid="save-pin-btn"
                >
                  {saving ? 'Saving...' : 'Save PIN'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Temporary Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="sm:max-w-md border-2 border-emerald-200">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-900">
                <KeyRound className="w-5 h-5 text-emerald-600" />
                Temporary Password
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm text-slate-600">
                Share this temporary password with the staff member. They will be required to change it on first login.
              </p>
              
              <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <code className="text-lg font-mono text-slate-900">{tempPassword}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyPassword}
                    className="border-2"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                <strong>Important:</strong> This password will only be shown once. Make sure to copy it before closing.
              </div>
              
              <Button
                onClick={() => {
                  setShowPasswordModal(false);
                  setTempPassword('');
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="close-password-modal"
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PIN Verification Modal */}
        <PinModal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setPendingAction(null);
          }}
          onVerify={executeAction}
          title={pendingAction?.type === 'reset_password' ? 'Reset Password' : 'Confirm Action'}
          description={
            pendingAction?.type === 'reset_password'
              ? `Enter your PIN to reset password for ${pendingAction?.staffName}`
              : `Enter your PIN to ${pendingAction?.newStatus ? 'activate' : 'deactivate'} ${pendingAction?.staffName}`
          }
          loading={saving}
        />
      </div>
    </Layout>
  );
};

export default StaffManagement;
